// src/shared/context/active-doctors-context.js
import React, { createContext, useState, useEffect, useContext } from "react";
import { AuthContext } from "./auth-context";

export const ActiveDoctorsContext = createContext({
  activeDoctorIds: [],
  availableDoctors: [],
  toggleDoctor: () => {},
  setActiveDoctors: () => {},
  isActiveDoctorId: () => false,
  clearActiveDoctors: () => {},
});

export function ActiveDoctorsProvider({ children }) {
  const auth = useContext(AuthContext);
  const [availableDoctors, setAvailableDoctors] = useState([]);
  const [activeDoctorIds, setActiveDoctorIds] = useState([]);

  // Initialize available doctors and set default active doctors
  useEffect(() => {
    if (
      auth.usuariosAssociados &&
      ["Assistente", "Secretária(o)"].includes(auth.role)
    ) {
      // Extract doctors from auth context
      const doctors = auth.usuariosAssociados.map((medico) => ({
        id: medico.id,
        nome: medico.nome,
        cor: medico.cor || "#175c93", // Default color if not provided
      }));

      setAvailableDoctors(doctors);

      // If no active doctors are set, set them all as active by default
      if (activeDoctorIds.length === 0 && doctors.length > 0) {
        const allDoctorIds = doctors.map((doctor) => doctor.id);
        setActiveDoctorIds(allDoctorIds);

        // Persist to localStorage
        localStorage.setItem("activeDoctorIds", JSON.stringify(allDoctorIds));
      }
    }
  }, [auth.usuariosAssociados, auth.role]);

  // Load active doctors from localStorage on initial render
  useEffect(() => {
    const storedActiveDoctors = localStorage.getItem("activeDoctorIds");
    if (storedActiveDoctors) {
      try {
        const parsedDoctors = JSON.parse(storedActiveDoctors);
        setActiveDoctorIds(parsedDoctors);
      } catch (error) {
        console.error("Error parsing stored active doctors:", error);
        localStorage.removeItem("activeDoctorIds");
      }
    }
  }, []);

  // Toggle a doctor's active status
  const toggleDoctor = (doctorId) => {
    setActiveDoctorIds((prevIds) => {
      let newIds;
      if (prevIds.includes(doctorId)) {
        // Don't allow removing the last active doctor
        if (prevIds.length === 1) {
          return prevIds;
        }
        newIds = prevIds.filter((id) => id !== doctorId);
      } else {
        newIds = [...prevIds, doctorId];
      }

      // Persist to localStorage
      localStorage.setItem("activeDoctorIds", JSON.stringify(newIds));
      return newIds;
    });
  };

  // Set multiple active doctors at once
  const setActiveDoctors = (doctorIds) => {
    if (doctorIds && doctorIds.length > 0) {
      setActiveDoctorIds(doctorIds);
      localStorage.setItem("activeDoctorIds", JSON.stringify(doctorIds));
    }
  };

  // Check if a doctor is active
  const isActiveDoctorId = (doctorId) => {
    return activeDoctorIds.includes(doctorId);
  };

  // Clear all active doctors
  const clearActiveDoctors = () => {
    setActiveDoctorIds([]);
    localStorage.removeItem("activeDoctorIds");
  };

  return (
    <ActiveDoctorsContext.Provider
      value={{
        activeDoctorIds,
        availableDoctors,
        toggleDoctor,
        setActiveDoctors,
        isActiveDoctorId,
        clearActiveDoctors,
      }}
    >
      {children}
    </ActiveDoctorsContext.Provider>
  );
}

// Custom hook for easier context use
export function useActiveDoctors() {
  return useContext(ActiveDoctorsContext);
}
